/**
 * Tiny typed builder for RSQL/FIQL expressions, the query syntax accepted
 * by the `search` query param of every Nawam back-end list endpoint
 * (`findAll`).
 *
 * Operators :
 *   ==  !=  =lt= (<)  =le= (<=)  =gt= (>)  =ge= (>=)
 *   =in=(...)  =out=(...)
 *   AND : `;`     OR : `,`
 *   wildcard : `*`   grouping : `(...)`
 *
 * Quoting strategy :
 *   - Numbers and booleans are emitted as-is.
 *   - Strings made of safe chars (letters, digits, `. _ - @ +`) are emitted
 *     unquoted so the `*` wildcard works directly (e.g. `firstName==*john*`).
 *   - Strings containing anything else are wrapped in single quotes with
 *     internal `'` and `\` escaped. Wildcards inside quoted values may or
 *     may not be honored by the back-end RSQL parser — if you need a
 *     wildcard, prefer a safe value.
 *   - `like` / `startsWith` / `endsWith` fall back to exact-equality when
 *     the fragment has unsafe chars, so the request still succeeds even
 *     if wildcards are stripped.
 *
 * Combinators (`and`, `or`) skip `null`/`undefined` parts — drop a
 * conditional filter by emitting `null`, the surrounding expression
 * stays valid.
 */

export type Rsql = string | null | undefined;

const SAFE_VALUE_RE = /^[A-Za-z0-9._\-@+]+$/;

/** Internal: format a value for emission inside an RSQL expression. */
function rsqlValue(v: unknown): string {
  if (v === null || v === undefined) return "''";
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  const s = String(v);
  if (SAFE_VALUE_RE.test(s)) return s;
  return `'${s.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`;
}

function isEmpty(v: unknown): boolean {
  return v === null || v === undefined || v === '';
}

export const rsql = {
  eq:  (field: string, value: unknown): Rsql => isEmpty(value) ? null : `${field}==${rsqlValue(value)}`,
  ne:  (field: string, value: unknown): Rsql => isEmpty(value) ? null : `${field}!=${rsqlValue(value)}`,
  lt:  (field: string, value: unknown): Rsql => isEmpty(value) ? null : `${field}=lt=${rsqlValue(value)}`,
  le:  (field: string, value: unknown): Rsql => isEmpty(value) ? null : `${field}=le=${rsqlValue(value)}`,
  gt:  (field: string, value: unknown): Rsql => isEmpty(value) ? null : `${field}=gt=${rsqlValue(value)}`,
  ge:  (field: string, value: unknown): Rsql => isEmpty(value) ? null : `${field}=ge=${rsqlValue(value)}`,

  /** `field==*fragment*` — contains. Falls back to exact eq if fragment has unsafe chars. */
  like: (field: string, fragment: unknown): Rsql => {
    if (isEmpty(fragment)) return null;
    const s = String(fragment).trim();
    if (!s) return null;
    if (SAFE_VALUE_RE.test(s)) return `${field}==*${s}*`;
    return `${field}==${rsqlValue(s)}`;
  },

  startsWith: (field: string, prefix: unknown): Rsql => {
    if (isEmpty(prefix)) return null;
    const s = String(prefix).trim();
    if (!s) return null;
    if (SAFE_VALUE_RE.test(s)) return `${field}==${s}*`;
    return `${field}==${rsqlValue(s)}`;
  },

  endsWith: (field: string, suffix: unknown): Rsql => {
    if (isEmpty(suffix)) return null;
    const s = String(suffix).trim();
    if (!s) return null;
    if (SAFE_VALUE_RE.test(s)) return `${field}==*${s}`;
    return `${field}==${rsqlValue(s)}`;
  },

  in:  (field: string, values: readonly unknown[]): Rsql =>
    !values || values.length === 0 ? null : `${field}=in=(${values.map(rsqlValue).join(',')})`,

  out: (field: string, values: readonly unknown[]): Rsql =>
    !values || values.length === 0 ? null : `${field}=out=(${values.map(rsqlValue).join(',')})`,

  /** AND-combine (`;`). Null/undefined parts dropped. Returns `null` if all dropped. */
  and: (...parts: Rsql[]): Rsql => {
    const kept = parts.filter((p): p is string => typeof p === 'string' && p.length > 0);
    if (kept.length === 0) return null;
    if (kept.length === 1) return kept[0];
    return kept.join(';');
  },

  /**
   * OR-combine (`,`). Any part that itself contains a top-level AND (`;`) is
   * wrapped in parens to keep precedence (AND binds tighter than OR in RSQL).
   * Null/undefined parts dropped.
   */
  or: (...parts: Rsql[]): Rsql => {
    const kept = parts.filter((p): p is string => typeof p === 'string' && p.length > 0);
    if (kept.length === 0) return null;
    if (kept.length === 1) return kept[0];
    return kept.map((p) => (p.includes(';') ? `(${p})` : p)).join(',');
  },

  /** Wrap in parens to force precedence. */
  group: (expr: Rsql): Rsql => (expr ? `(${expr})` : null),
};

/**
 * Convenience helper for a free-text search bar : runs a `contains` on each
 * field and OR-combines them. Returns `null` for empty queries so the caller
 * can pass `undefined` to the service (= no `search` param sent).
 *
 *   rsqlTextSearch(['firstName', 'lastName', 'email'], 'deke')
 *   // → "firstName==*deke*,lastName==*deke*,email==*deke*"
 */
export function rsqlTextSearch(
  fields: readonly string[],
  query: string | null | undefined,
): Rsql {
  const q = (query ?? '').trim();
  if (!q) return null;
  return rsql.or(...fields.map((f) => rsql.like(f, q)));
}
